import { MutableRefObject, useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGlobalReducer } from "../../GlobalContext";
import { Create, SaveRecordWithMethod, Update } from "../api/recordService";
import { addSuccessNotification } from "../shell/notification/notifications";
import { StageRow } from "../config/pageRows.types";
import { getAllFormAttributes, getRecordName } from "./entityPagePayload";
import { withParam } from "./entityPageUrl";
import { EntityRecordRead, SavingAction } from "./EntityPageProvider.types";
import {
  PageSaveOption,
  RecordDefinition,
  SaveConfirmation,
} from "./recordDefinition.types";
import { submitsOnce } from "./submitsOnce";

/** A save waiting on the definition's confirmation. */
interface PendingSave {
  confirmation: SaveConfirmation;
  closeAfter: boolean;
  option?: PageSaveOption;
  resolve: (saved: boolean) => void;
}

/**
 * Creating or updating the record, then reading back what the server made of it.
 * Resolves true once saved, so a caller can move on only after a save that worked.
 */
export function useEntityPageSave({
  entityName,
  definition,
  record,
  originalRecord,
  rows,
  validate,
  readRecord,
  applyRead,
  skipNextFetch,
  settleView,
  setOriginalRecord,
  goToCloseRoute,
  allowNavigationRef,
}: {
  entityName: string;
  definition: RecordDefinition | undefined;
  record: Record<string, unknown> | null;
  originalRecord: Record<string, unknown> | null;
  rows: StageRow[];
  validate: () => boolean;
  readRecord: (recordId: string) => Promise<EntityRecordRead>;
  applyRead: (read: EntityRecordRead) => void;
  skipNextFetch: () => void;
  /** Keeps the view a new record was created on, rather than resolving one again. */
  settleView: (recordId: string) => void;
  setOriginalRecord: (record: Record<string, unknown> | null) => void;
  goToCloseRoute: () => void;
  /** Lets this save's own navigations past the unsaved-changes guard. */
  allowNavigationRef: MutableRefObject<boolean>;
}) {
  const globalDispatch = useGlobalReducer();
  const [, setSearchParams] = useSearchParams();
  const [savingAction, setSavingAction] = useState<SavingAction>(null);
  const [pendingSave, setPendingSave] = useState<PendingSave | null>(null);

  const save = useCallback(
    async (closeAfter: boolean, option?: PageSaveOption): Promise<boolean> => {
      if (!record) {
        return false;
      }

      // The option's values are part of what is saved, so they are validated and sent
      // with the rest.
      const current = option ? { ...record, ...option.patch(record) } : record;

      // A record read by its own method is the organisation's one, so never new.
      const isCreating = !current.id && !definition?.readMethod;
      const leavesAfterSave = submitsOnce(definition);

      setSavingAction(
        option ? `save:${option.id}` : closeAfter ? "save-and-close" : "save"
      );
      try {
        const toSend = definition?.beforeSave
          ? await definition.beforeSave(
              current,
              isCreating ? null : originalRecord,
              globalDispatch
            )
          : current;
        const formAttributes = definition?.toSavePayload
          ? (definition.toSavePayload(toSend) as Record<string, unknown>)
          : getAllFormAttributes(rows, toSend);
        const name = getRecordName(rows, toSend);
        // The success toast is raised once the re-read below has finished too, so it
        // doesn't appear while the spinner is still running.
        const response = definition?.saveMethod
          ? await SaveRecordWithMethod(
              globalDispatch,
              definition.saveMethod,
              definition.toSavePayload ? definition.toSavePayload(toSend) : toSend
            )
          : isCreating
          ? await Create(
              globalDispatch,
              "",
              { Id: undefined, Name: name, Attributes: formAttributes },
              entityName
            )
          : await Update(
              globalDispatch,
              "",
              { Id: current.id, Name: name, Attributes: formAttributes },
              entityName
            );

        // An update can answer with an empty 200; a create has to return the new id.
        const saved =
          Boolean(response?.data) ||
          (!isCreating &&
            Boolean(response) &&
            !(response instanceof Error) &&
            response.status >= 200 &&
            response.status < 300);

        if (saved) {
          definition?.afterSave?.(current, globalDispatch);

          const runAfterSaved = (read: EntityRecordRead) => {
            if (read.record) {
              definition?.afterSaved?.(
                read.record,
                current,
                isCreating ? null : originalRecord,
                globalDispatch
              );
            }
          };

          if (leavesAfterSave) {
            // What it answers is no id this page can read, so there is nothing to refresh.
            setOriginalRecord(current);
          } else if (isCreating) {
            // Create only returns the new id, so read the persisted record. Done here
            // rather than via the id-change effect, so the Save button's spinner covers it.
            const newId = String(response.data);
            skipNextFetch();
            settleView(newId);
            // Built from the current params, not replacing them - a chosen view has to
            // survive the record gaining an id. The record is not being left.
            allowNavigationRef.current = true;
            setSearchParams((previous) => withParam(previous, "id", newId));
            allowNavigationRef.current = false;

            const read = await readRecord(newId);
            applyRead(read);
            runAfterSaved(read);
          } else if (closeAfter) {
            // Leaving the page, so there's nothing to refresh.
            setOriginalRecord(current);
          } else {
            // Re-read: the server sets fields the payload never carried.
            const read = await readRecord(String(current.id ?? ""));
            applyRead(read);
            runAfterSaved(read);
          }

          addSuccessNotification(
            globalDispatch,
            isCreating ? "Record created" : "Record updated"
          );

          if (closeAfter || leavesAfterSave) {
            // Saved, so leaving loses nothing. Not reset: the page is going.
            allowNavigationRef.current = true;
            goToCloseRoute();
          }

          return true;
        }

        return false;
      } finally {
        setSavingAction(null);
      }
    },
    [
      record,
      originalRecord,
      definition,
      rows,
      entityName,
      globalDispatch,
      goToCloseRoute,
      setSearchParams,
      readRecord,
      applyRead,
      skipNextFetch,
      settleView,
      setOriginalRecord,
      allowNavigationRef,
    ]
  );

  const onSave = useCallback(
    async (closeAfter: boolean, option?: PageSaveOption): Promise<boolean> => {
      if (!record || !validate()) {
        return false;
      }

      const confirmation =
        option?.confirmation ??
        definition?.saveConfirmation?.(
          option ? { ...record, ...option.patch(record) } : record,
          originalRecord
        );

      if (!confirmation) {
        return save(closeAfter, option);
      }

      // Resolved by the confirmation modal, so a caller awaiting the save waits for it.
      return new Promise<boolean>((resolve) =>
        setPendingSave({ confirmation, closeAfter, option, resolve })
      );
    },
    [record, validate, definition, originalRecord, save]
  );

  const confirmSave = useCallback(async () => {
    if (!pendingSave) {
      return;
    }

    const saved = await save(pendingSave.closeAfter, pendingSave.option);
    setPendingSave(null);
    pendingSave.resolve(saved);
  }, [pendingSave, save]);

  const cancelSave = useCallback(() => {
    pendingSave?.resolve(false);
    setPendingSave(null);
  }, [pendingSave]);

  return {
    savingAction,
    onSave,
    saveConfirmation: pendingSave?.confirmation ?? null,
    confirmSave,
    cancelSave,
  };
}
